import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import CDS from "design-system";
import UserSelectField from "components/FormElements/Fields/SelectFields/UserSelectField";
import { User } from "features/User/models";
import { LeadPerson } from "features/Resources/Meeting/models";
import { useSetMeetingLead } from "features/Resources/Meeting/hooks";

interface Props {
  meetingId: string;
  meetingType: string;
  status: string;
  lead?: LeadPerson;
  /** Called after a successful reassignment — lets a caller refresh its own query (e.g. bulk preview). */
  onLeadChanged?: () => void;
}

function LeadDisplay({ lead, interactive = false }: { lead: LeadPerson; interactive?: boolean }) {
  return (
    <HStack spacing={2} align="center" minW={0}>
      <Avatar name={lead.name} src={lead.avatarUrl || undefined} size="2xs" />
      <CDS.Text14 noOfLines={1} color={interactive ? undefined : "content.secondary"}>
        {lead.name}
      </CDS.Text14>
    </HStack>
  );
}

export default function MeetingLeadCell({ meetingId, meetingType, status, lead, onLeadChanged }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { control } = useForm();
  const { mutate, isPending } = useSetMeetingLead(meetingId);
  const [pendingLead, setPendingLead] = useState<User>();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // 1:1 / individual meetings are already the host's meeting — no separate lead.
  if (meetingType === "individual") {
    return <CDS.Text14 color="content.tertiary">—</CDS.Text14>;
  }

  // Recurring group sessions show their lead but it's owned by the series; past
  // sessions are frozen. Both render read-only.
  const editable = meetingType === "group_session_adhoc" && status !== "completed";
  if (!editable) {
    return lead ? <LeadDisplay lead={lead} /> : <CDS.Text14 color="content.tertiary">—</CDS.Text14>;
  }

  const isScheduled = status === "scheduled";

  const handleSelect = (user?: User) => {
    onClose();
    if (!user || String(user.id) === lead?.id) return;
    setPendingLead(user);
  };

  const confirmChange = () => {
    if (!pendingLead) return;
    mutate(
      { leadId: pendingLead.id },
      {
        onSuccess: () => {
          onLeadChanged?.();
          setPendingLead(undefined);
        },
      },
    );
  };

  return (
    <>
      <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" isLazy>
        <PopoverTrigger>
          <Box
            as="button"
            type="button"
            textAlign="left"
            borderRadius="md"
            px={1.5}
            py={1}
            _hover={{ bg: "bg.subtle" }}
            maxW="200px"
          >
            {lead ? (
              <LeadDisplay lead={lead} interactive />
            ) : (
              <CDS.Text14 color="content.info" fontWeight="medium">
                + Assign lead
              </CDS.Text14>
            )}
          </Box>
        </PopoverTrigger>
        <PopoverContent w="320px">
          <PopoverArrow />
          <PopoverBody>
            <UserSelectField
              name="leadId"
              control={control}
              placeholder="Search people..."
              onChangeCallback={handleSelect}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <AlertDialog isOpen={!!pendingLead} leastDestructiveRef={cancelRef} onClose={() => setPendingLead(undefined)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Make {pendingLead?.fullName} the lead?
            </AlertDialogHeader>
            <AlertDialogBody>
              <CDS.Stack spacing={CDS.Spacing.tiny}>
                <CDS.Text14 color="content.secondary">
                  This changes the lead for <b>everyone in this session</b> in Camino.
                </CDS.Text14>
                {isScheduled && (
                  <CDS.Text14 color="content.secondary">
                    The calendar invite is updated too — {pendingLead?.fullName} is added as the host and notified.
                  </CDS.Text14>
                )}
              </CDS.Stack>
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <CDS.Button
                ref={cancelRef}
                intent={CDS.Button.intent.secondary}
                onClick={() => setPendingLead(undefined)}
              >
                Cancel
              </CDS.Button>
              <CDS.Button intent={CDS.Button.intent.primary} onClick={confirmChange} loading={isPending}>
                Change lead
              </CDS.Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
